"use client";

import Link from "next/link";
import { useCallback, useEffect, useRef, useState } from "react";

interface SearchResult {
  type: "template" | "integration" | "blog";
  title: string;
  url: string;
  description: string;
}

interface SearchResponse {
  results: SearchResult[];
}

const TYPE_LABELS: Record<string, string> = {
  template: "Templates",
  integration: "Integrations",
  blog: "Blog",
};

export function PublicSearchBar() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchResults = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`/api/public-search?q=${encodeURIComponent(q)}&limit=10`);
      if (res.ok) {
        const data = (await res.json()) as SearchResponse;
        setResults(data.results);
      }
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchResults(query), 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, fetchResults]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Group results by type
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    const key = r.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div ref={containerRef} className="relative w-full max-w-md">
      <input
        type="text"
        value={query}
        onChange={e => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder="Search templates, integrations..."
        className="bg-ds-surface-primary border-ds-border text-ds-text-primary placeholder:text-ds-text-muted focus:border-ds-neutral-500 w-full rounded-lg border px-4 py-2 text-sm outline-none transition-colors"
        aria-label="Search B3OS"
      />
      {isOpen && query.length >= 2 && (
        <div className="bg-ds-surface-primary border-ds-border absolute left-0 right-0 top-full z-50 mt-1 max-h-80 overflow-y-auto rounded-lg border shadow-lg">
          {isLoading && <div className="text-ds-text-muted p-3 text-sm">Searching...</div>}
          {!isLoading && results.length === 0 && <div className="text-ds-text-muted p-3 text-sm">No results found</div>}
          {!isLoading &&
            Object.entries(grouped).map(([type, items]) => (
              <div key={type}>
                <div className="text-ds-text-muted border-ds-border border-b px-3 py-1.5 text-xs font-medium uppercase">
                  {TYPE_LABELS[type] || type}
                </div>
                {items.map(item => (
                  <Link
                    key={item.url}
                    href={item.url}
                    className="hover:bg-ds-surface-secondary block px-3 py-2 transition-colors"
                    onClick={() => setIsOpen(false)}
                  >
                    <div className="text-ds-text-primary text-sm font-medium">{item.title}</div>
                    {item.description && (
                      <div className="text-ds-text-muted mt-0.5 line-clamp-1 text-xs">{item.description}</div>
                    )}
                  </Link>
                ))}
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
